import { Motion, Presence } from "@repo/solid-motionone";
import {
  TbArrowBackUp,
  TbArrowForwardUp,
  TbAt,
  TbBolt,
  TbBook,
  TbBrandMeta,
  TbBrandOpenai,
  TbBrandOpenSource,
  TbPaperclip,
  TbSlash,
} from "solid-icons/tb";
import { type Component, createSignal, type JSX, type ParentComponent, Show } from "solid-js";
import { KeyboardShortcutPill } from "@core/components/_original/KeyboardShortcutPill";
import { StButton } from "@core/components/_original/StButton";
import { Tooltip, TooltipTrigger } from "@core/components/_original/Tooltip";
import { StBrandAnthropic, StBrandDeepSeek, type StIconComponent } from "@core/components/icons";
import { useWire } from "@core/wire";
import { usePromptContext } from "../PromptContext";
import { StDropdown } from "@core/components/_original/StDropdown";
import { modelDisplayName, modelVendor } from "@repo/client";
import { StBrandGemini } from "@core/components/icons/StBrandGemini";
import { A } from "@solidjs/router";
import { useUIState } from "@core/ui/UIState";
import { PromptLibraryDrawer } from "@core/screens/drawer/PromptLibraryDrawer";
import { Switch as KSwitch } from "@kobalte/core";

export const ChatBottomBar: Component = () => {
  const { focused, editor, setShowUploadModal, promptRef, rawProps, mentionsState } = usePromptContext();
  const ui = useUIState();
  const wire = useWire();

  const [selectedModel, setSelectedModel] = ui.llmModelOverride;

  const [, setPromptLibraryDrawerOpen] = ui.promptLibraryDrawer;
  const [autoImprovePrompt, setAutoImprovePrompt] = ui.autoImprovePrompt;

  return (
    <Show when={focused() || rawProps.alwaysBottomBar}>
      <div class="flex items-center justify-between gap-2 text-2xs text-gray-600 dark:text-gray-300 rounded-b px-2 py-2">
        <div class="flex items-center flex-wrap gap-2">
          <ToolbarButton label="Slash command" onClick={() => editor()?.commands.invokeSlashCommand()} icon={TbSlash} />
          <Show when={wire.isApp()}>
            <ToolbarButton
              label="Upload files"
              disabled={wire.services.auth.isConnecting()}
              onClick={() => {
                setShowUploadModal(true);
              }}
              icon={TbPaperclip}
            />
          </Show>
          <ToolbarButton
            label="Specify a Collection or an Asset"
            onClick={() => mentionsState.activateMention(editor())}
            icon={TbAt}
          />

          <Show when={!wire.services.inputHistory.isFirst()}>
            <ToolbarButton
              label="Previous prompt"
              tooltip={
                <p>
                  Previous prompt{" "}
                  <KeyboardShortcutPill
                    keys={["ArrowUp"]}
                    class="ml-1 px-1.5 py-0 border text-gray-100 dark:text-slate-900 bg-slate-800 dark:bg-slate-300 border-slate-500 dark:border-slate-400 text-sm leading-6 font-black"
                  />
                </p>
              }
              onClick={() => {
                wire.services.inputHistory.getPrevious(editor());
                editor()?.commands.focus("end");
              }}
              icon={TbArrowBackUp}
            />
          </Show>
          <Show when={!wire.services.inputHistory.isLast()}>
            <ToolbarButton
              label="Next prompt"
              tooltip={
                <span>
                  Next prompt{" "}
                  <KeyboardShortcutPill
                    keys={["ArrowDown"]}
                    class="ml-1 px-1.5 py-0 border text-gray-100 dark:text-slate-900 bg-slate-800 dark:bg-slate-300 border-slate-500 dark:border-slate-400 text-sm leading-6 font-black"
                  />
                </span>
              }
              onClick={() => {
                wire.services.inputHistory.getNext(editor());
                editor()?.commands.focus("end");
              }}
              icon={TbArrowForwardUp}
            />
          </Show>

          <div class="flex justify-center animate-fade-in">
            <StButton
              class="w-auto bg-indigo-600 text-white hover:bg-indigo-600"
              size="sm"
              icon={TbBook}
              onClick={() => {
                setPromptLibraryDrawerOpen(true);
              }}
            >
              Prompt Library
            </StButton>
          </div>

          <KSwitch.Root
            class="flex items-center gap-2 cursor-pointer"
            onChange={setAutoImprovePrompt}
            checked={autoImprovePrompt()}
          >
            <KSwitch.Label class="flex items-center text-2xs select-none cursor-pointer gap-1">
              Improve prompt
            </KSwitch.Label>
            <KSwitch.Input class="peer" />
            <KSwitch.Control class="inline-flex items-center h-6 w-11 border border-surface-decoration rounded-full px-0.5 bg-surface transition-colors duration-250 data-[checked]:border-primary data-[checked]:bg-primary peer-focus:ring-2 peer-focus:ring-primary">
              <KSwitch.Thumb class="h-5 w-5 rounded-full bg-white transition-transform duration-250 data-[checked]:translate-x-[calc(100%-1px)]" />
            </KSwitch.Control>
          </KSwitch.Root>

          <PromptLibraryDrawer />
        </div>

        <Show
          when={wire.services.auth.isVerified()}
          fallback={
            <Tooltip
              contentClass="z-max"
              content={
                <p>
                  <A href="/auth/signup" class="underline">
                    Sign up or log in
                  </A>{" "}
                  to pick specific LLMs to respond
                </p>
              }
              theme="dark"
              placement="top"
            >
              <TooltipTrigger as="span">
                <StButton icon={TbBolt} size="sm">
                  {modelDisplayName(selectedModel() || "DynamicLLM")}
                </StButton>
              </TooltipTrigger>
            </Tooltip>
          }
        >
          <StDropdown
            theme="dark"
            opts={{ placement: "bottom-end" }}
            trigger={{ as: "span" }}
            content={{ class: "z-[1000]" }}
            items={[
              {
                kind: "item",
                content: (
                  <span class="flex flex-col gap-1">
                    <span class="leading-none font-medium">DynamicLLM</span>
                    <span class="leading-none text-xs text-blue-200">Let Storytell pick the best model</span>
                  </span>
                ),
                icon: TbBolt,
                props: {
                  onClick: () => setSelectedModel(""),
                  class: `h-12${selectedModel() === "" ? " bg-blue-800" : ""}`,
                },
              },
              {
                kind: "menu",
                trigger: {
                  content: "Gemini",
                  icon: StBrandGemini,
                  props: {
                    class: modelVendor(selectedModel() || "") === "google" ? "bg-blue-900" : "",
                  },
                },
                items: [
                  {
                    kind: "item",
                    content: "Gemini 2.5 Pro",
                    icon: StBrandGemini,
                    props: {
                      onClick: () => setSelectedModel("google-gemini-2.5-pro"),
                      class: selectedModel() === "google-gemini-2.5-pro" ? "bg-blue-800" : "",
                    },
                  },
                  {
                    kind: "item",
                    content: "Gemini 2.0 Flash",
                    icon: StBrandGemini,
                    props: {
                      onClick: () => setSelectedModel("google-gemini-2.0-flash"),
                      class: selectedModel() === "google-gemini-2.0-flash" ? "bg-blue-800" : "",
                    },
                  },
                  {
                    kind: "item",
                    content: "Gemini 2.5 Flash",
                    icon: StBrandGemini,
                    props: {
                      onClick: () => setSelectedModel("google-gemini-2.5-flash"),
                      class: selectedModel() === "google-gemini-2.5-flash" ? "bg-blue-800" : "",
                    },
                  },
                  {
                    kind: "item",
                    content: "Gemini 2.5 Flash Thinking",
                    icon: StBrandGemini,
                    props: {
                      onClick: () => setSelectedModel("google-gemini-2.5-flash-thinking"),
                      class: selectedModel() === "google-gemini-2.5-flash-thinking" ? "bg-blue-800" : "",
                    },
                  },
                ],
              },
              {
                kind: "menu",
                trigger: {
                  content: "OpenAI",
                  icon: TbBrandOpenai,
                  props: {
                    class: modelVendor(selectedModel() || "") === "openai" ? "bg-blue-900" : "",
                  },
                },
                items: [
                  {
                    kind: "item",
                    content: "GPT-4.1",
                    icon: TbBrandOpenai,
                    props: {
                      onClick: () => setSelectedModel("openai-gpt-4.1"),
                      class: selectedModel() === "openai-gpt-4.1" ? "bg-blue-800" : "",
                    },
                  },
                  {
                    kind: "item",
                    content: "GPT-4.1 Mini",
                    icon: TbBrandOpenai,
                    props: {
                      onClick: () => setSelectedModel("openai-gpt-4.1-mini"),
                      class: selectedModel() === "openai-gpt-4.1-mini" ? "bg-blue-800" : "",
                    },
                  },
                  {
                    kind: "item",
                    content: "GPT-4.1 Nano",
                    icon: TbBrandOpenai,
                    props: {
                      onClick: () => setSelectedModel("openai-gpt-4.1-nano"),
                      class: selectedModel() === "openai-gpt-4.1-nano" ? "bg-blue-800" : "",
                    },
                  },
                  {
                    kind: "item",
                    content: "O3",
                    icon: TbBrandOpenai,
                    props: {
                      onClick: () => setSelectedModel("openai-o3"),
                      class: selectedModel() === "openai-o3" ? "bg-blue-800" : "",
                    },
                  },
                  {
                    kind: "item",
                    content: "O4 Mini",
                    icon: TbBrandOpenai,
                    props: {
                      onClick: () => setSelectedModel("openai-o4-mini"),
                      class: selectedModel() === "openai-o4-mini" ? "bg-blue-800" : "",
                    },
                  },
                ],
              },
              {
                kind: "menu",
                trigger: {
                  content: "Anthropic",
                  icon: StBrandAnthropic,
                  props: {
                    class: modelVendor(selectedModel() || "") === "anthropic" ? "bg-blue-900" : "",
                  },
                },
                items: [
                  {
                    kind: "item",
                    content: "Claude 3.7 Sonnet",
                    icon: StBrandAnthropic,
                    props: {
                      onClick: () => setSelectedModel("anthropic-claude-3-7-sonnet"),
                      class: selectedModel() === "anthropic-claude-3-7-sonnet" ? "bg-blue-800" : "",
                    },
                  },
                  {
                    kind: "item",
                    content: "Claude 3.7 Sonnet Thinking",
                    icon: StBrandAnthropic,
                    props: {
                      onClick: () => setSelectedModel("anthropic-claude-3-7-sonnet-thinking"),
                      class: selectedModel() === "anthropic-claude-3-7-sonnet-thinking" ? "bg-blue-800" : "",
                    },
                  },
                  {
                    kind: "item",
                    content: "Claude 3.5 Sonnet",
                    icon: StBrandAnthropic,
                    props: {
                      onClick: () => setSelectedModel("anthropic-claude-3-5-sonnet"),
                      class: selectedModel() === "anthropic-claude-3-5-sonnet" ? "bg-blue-800" : "",
                    },
                  },
                ],
              },
              {
                kind: "menu",
                trigger: {
                  content: "Open Source",
                  icon: TbBrandOpenSource,
                  props: {
                    class:
                      modelVendor(selectedModel() || "") === "deepseek" || modelVendor(selectedModel() || "") === "meta"
                        ? "bg-blue-800"
                        : "",
                  },
                },
                items: [
                  {
                    kind: "item",
                    content: "DeepSeek R1 Distill Llama 70B",
                    icon: StBrandDeepSeek,
                    props: {
                      onClick: () => setSelectedModel("deepseek-r1-distill-llama-70b"),
                      class: selectedModel() === "deepseek-r1-distill-llama-70b" ? "bg-blue-800" : "",
                    },
                  },
                  {
                    kind: "item",
                    content: "DeepSeek R1 Distill Qwen 7B",
                    icon: StBrandDeepSeek,
                    props: {
                      onClick: () => setSelectedModel("deepseek-r1-distill-qwen-7b"),
                      class: selectedModel() === "deepseek-r1-distill-qwen-7b" ? "bg-blue-800" : "",
                    },
                  },
                  {
                    kind: "item",
                    content: "Llama 3.3 70B",
                    icon: TbBrandMeta,
                    props: {
                      onClick: () => setSelectedModel("meta-llama-3.3-70b-versatile"),
                      class: selectedModel() === "meta-llama-3.3-70b-versatile" ? "bg-blue-800" : "",
                    },
                  },
                  {
                    kind: "item",
                    content: "Llama 4 Scout",
                    icon: TbBrandMeta,
                    props: {
                      onClick: () => setSelectedModel("meta-llama-4-scout"),
                      class: selectedModel() === "meta-llama-4-scout" ? "bg-blue-800" : "",
                    },
                  },
                  {
                    kind: "item",
                    content: "Llama 4 Maverick",
                    icon: TbBrandMeta,
                    props: {
                      onClick: () => setSelectedModel("meta-llama-4-maverick"),
                      class: selectedModel() === "meta-llama-4-maverick" ? "bg-blue-800" : "",
                    },
                  },
                ],
              },
            ]}
            mount={promptRef()}
          >
            <StButton icon={TbBolt} size="sm">
              {modelDisplayName(selectedModel() || "DynamicLLM")}
            </StButton>
          </StDropdown>
        </Show>
      </div>
    </Show>
  );
};

const ToolbarButton: ParentComponent<{
  label: string;
  tooltip?: JSX.Element;
  onClick?: () => void;
  icon: StIconComponent;
  disabled?: boolean;
}> = (props) => {
  return (
    <Tooltip theme="invert" placement="top" content={props.tooltip ?? <p>{props.label}</p>}>
      <TooltipTrigger as="span">
        <StButton size="sm" onClick={props.onClick} icon={props.icon} disabled={props.disabled} label={props.label} />
      </TooltipTrigger>
    </Tooltip>
  );
};
